<template>
  <h1 class="sr-only">{{ t('support.myTicketsTitle') }}</h1>
  <PageToolbar>
    <template #actions>
      <Button :disabled="!selectedSlug" @click="openCreate">{{ t('support.newTicket') }}</Button>
    </template>
  </PageToolbar>

  <SupportLayout>
    <div v-if="loading" class="flex justify-center py-20">
      <LumiLoader size="page" :label="t('common.loading')" />
    </div>

    <Table fill v-else-if="tickets.length">
      <thead>
        <tr>
          <th>{{ t('support.subject') }}</th>
          <th>{{ t('support.module') }}</th>
          <th>{{ t('support.statusLabel') }}</th>
          <th>{{ t('support.updated') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="ticket in tickets"
          :key="ticket.id"
          class="cursor-pointer"
          @click="openTicket(ticket)"
        >
          <td class="font-medium text-heading">{{ ticket.subject }}</td>
          <td class="text-tertiary">{{ ticket.moduleKey }}</td>
          <td>
            <div class="flex flex-wrap items-center gap-1.5">
              <Badge :tone="ticketStatusTone(ticket.status)">{{ t(`support.status.${ticket.status}`) }}</Badge>
              <!-- Escalation is a state of its own, next to the status (#390):
                   an escalated ticket is with the sysadmins, whatever its status. -->
              <Badge v-if="ticket.escalatedAt" :tone="ticketEscalationTone()" data-test="ticket-escalated">
                {{ t('support.escalated') }}
              </Badge>
            </div>
          </td>
          <td class="text-tertiary whitespace-nowrap">{{ formatDate(ticket.updatedAt) }}</td>
        </tr>
      </tbody>
    </Table>

    <Card v-else>
      <EmptyState :title="t('support.empty.myTitle')" :description="t('support.empty.myBody')">
        <template #action>
          <Button :disabled="!selectedSlug" @click="openCreate">{{ t('support.newTicket') }}</Button>
        </template>
      </EmptyState>
    </Card>

    <SupportRequestDialog
      :open="createOpen"
      :title="t('support.newTicketTitle')"
      :subject-label="t('support.subject')"
      :subject-placeholder="t('support.subjectPlaceholder')"
      :message-label="t('support.message')"
      :message-placeholder="t('support.messagePlaceholder')"
      :submit-label="t('support.send')"
      :cancel-label="t('common.cancel')"
      :busy="creating"
      :submit-disabled="!consentChecked"
      :sent="createSent"
      :sent-message="t('support.sent')"
      :done-label="t('support.goToMyRequests')"
      @submit="handleCreate"
      @cancel="createOpen = false"
      @done="closeCreate"
    >
      <template #intro>{{ t('support.newTicketIntro') }}</template>
      <template #extra>
        <Checkbox v-model="consentChecked">{{ t('support.consentLabel') }}</Checkbox>
      </template>
    </SupportRequestDialog>
  </SupportLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  PageToolbar,
  LumiLoader,
  Table,
  SupportRequestDialog,
} from '@lumi/ui';
import { useApiMutation } from '@lumi/app-kit';
import SupportLayout from '@/components/SupportLayout.vue';
import { supportApi } from '@/utils/api';
import { createTicketBody, useSupportStore } from '@/stores/support';
import type { TicketDetail, TicketSummary } from '@/stores/support';
import { useTenantsStore } from '@/stores/tenants';
import { ticketEscalationTone, ticketStatusTone } from '@/utils/ticket-status';

const router = useRouter();
const { t, locale } = useI18n();
const supportStore = useSupportStore();
const tenantsStore = useTenantsStore();

// Tickets follow the shell's active tenant (the header switcher), so switching
// tenant reflows this list instead of staying pinned to the first tenant.
const selectedSlug = computed(() => tenantsStore.activeTenant?.slug || tenantsStore.hostTenantSlug || '');
const loading = ref(true);

const createOpen = ref(false);
// The request landed and the dialog says so, in place (#425).
const createSent = ref(false);
// inlineEdit: the dialog turns into its own receipt, so a toast would only repeat
// the sentence already on screen. Failures ride the default global channel — the
// shared SupportRequestDialog renders no error slot of its own, and the dialog
// stays open with the text intact so the send can be retried.
//
// It used to route into the ticket it had just created. That is what #425 is
// about: the person asked to send a request, not to be taken somewhere.
const { pending: creating, mutate: createTicket } = useApiMutation<TicketDetail, [string, string, string]>({
  tier: 'inlineEdit',
  run: async (slug, subject, message) => {
    const { data } = await supportApi.post(
      '/tickets',
      createTicketBody(subject, message),
      supportStore.tenantCfg(slug),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});
const consentChecked = ref(false);

const tickets = computed(() => supportStore.myTickets);

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleDateString(locale.value, { dateStyle: 'medium' });
  } catch {
    return value;
  }
}

function openTicket(ticket: TicketSummary) {
  router.push(`/support/t/${ticket.tenantSlug}/${ticket.id}`);
}

function openCreate() {
  consentChecked.value = false;
  createSent.value = false;
  createOpen.value = true;
}

// The receipt's one button. This view IS the list it names, so it closes and
// reloads rather than navigating — the new request has to be on screen for the
// label to have told the truth.
function closeCreate() {
  createOpen.value = false;
  createSent.value = false;
  void loadTickets();
}

async function handleCreate(payload: { subject: string; message: string }) {
  // The action trail travels with the body and is only sent because the consent
  // checkbox above was ticked — hence the gate here, before anything is built.
  if (!selectedSlug.value || !consentChecked.value) return;
  const outcome = await createTicket(selectedSlug.value, payload.subject, payload.message);
  if (!outcome.ok) return;
  createSent.value = true;
}

async function loadTickets() {
  if (!selectedSlug.value) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    await supportStore.loadMyTickets(selectedSlug.value);
  } finally {
    loading.value = false;
  }
}

// Reflow when the active tenant changes (header switcher).
watch(selectedSlug, loadTickets);

onMounted(async () => {
  // Shares the shell's tenant store (usually already loaded by the app chrome);
  // ensures it's populated even if this view mounts first.
  await tenantsStore.load();
  await loadTickets();
});
</script>
